// src/components/NotificationCenter.js
import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useNotifications, NOTIF_TYPES } from "../hooks/useNotifications";
import { useAuth } from "../hooks/useAuth";
import "./NotificationCenter.css";

function timeAgo(date) {
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60)   return "à l'instant";
  if (diff < 3600) return `${Math.floor(diff/60)}min`;
  if (diff < 86400)return `${Math.floor(diff/3600)}h`;
  return date.toLocaleDateString("fr-FR", { day:"numeric", month:"short" });
}

export default function NotificationCenter() {
  const { user } = useAuth();
  const { notifs, unreadCount, markRead, markAllRead } = useNotifications(user?.uid);
  const [open, setOpen] = useState(false);
  const panelRef = useRef();

  // Fermer en cliquant ailleurs
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  if (!user) return null;

  const handleOpen = () => {
    setOpen(o => !o);
  };

  const handleNotifClick = (notif) => {
    if (!notif.read) markRead(notif.id);
    setOpen(false);
  };

  const meta = (type) => NOTIF_TYPES[type] || { icon:"🔔", label:type, color:"var(--ash)" };

  return (
    <div className="notif-center" ref={panelRef}>
      {/* Bell button */}
      <button
        className={`notif-bell${unreadCount > 0 ? " has-unread" : ""}`}
        onClick={handleOpen}
        aria-label="Notifications"
      >
        <span className="notif-bell-icon">🔔</span>
        {unreadCount > 0 && (
          <span className="notif-badge">{unreadCount > 9 ? "9+" : unreadCount}</span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div className="notif-panel">
          <div className="notif-panel-header">
            <p className="notif-panel-title">NOTIFICATIONS</p>
            {unreadCount > 0 && (
              <button className="notif-mark-all" onClick={markAllRead}>
                TOUT LU
              </button>
            )}
          </div>

          <div className="notif-list">
            {notifs.length === 0 ? (
              <div className="notif-empty">
                <span>🌑</span>
                <p>Aucune notification démoniaque.</p>
              </div>
            ) : (
              notifs.map(notif => {
                const m = meta(notif.type);
                return (
                  <div
                    key={notif.id}
                    className={`notif-item${notif.read ? " read" : " unread"}`}
                    onClick={() => handleNotifClick(notif)}
                    style={{ "--notif-color": m.color }}
                  >
                    <div className="notif-item-left">
                      <span className="notif-item-icon">{m.icon}</span>
                      {!notif.read && <span className="notif-dot"/>}
                    </div>
                    <div className="notif-item-body">
                      {notif.link ? (
                        <Link to={notif.link} className="notif-item-title" onClick={() => setOpen(false)}>
                          {notif.title}
                        </Link>
                      ) : (
                        <p className="notif-item-title">{notif.title}</p>
                      )}
                      {notif.body && <p className="notif-item-body-text">{notif.body}</p>}
                      {notif.fromName && (
                        <p className="notif-item-from">
                          {notif.fromEmoji} {notif.fromName}
                        </p>
                      )}
                      <p className="notif-item-time">{timeAgo(notif.createdAt)}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {notifs.length > 0 && (
            <div className="notif-panel-footer">
              <p>{notifs.length} notification{notifs.length > 1 ? "s" : ""}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
